import { Alert, Card, Empty, Flex, Select, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Filters from "../../components/Filters";
import { useAuth } from "../../context/AuthContext";
import useEventsV2 from "../../hooks/useEventsV2";
import CabecalhoDoEvento from "../collections-change/CabecalhoDoEvento";
import EventoPorProdutoDetalhe from "../collections-change/EventoPorProdutoDetalhe";
import {
  JANELA_VIGENCIA,
  analisePorProduto,
  mensagemDeErro,
  parametrosDaJanela,
  periodoSemiaberto,
} from "../collections-change/promoHelpers";
import { opcoesDeEventos } from "../revenue/eventoHelpers";

const { Text } = Typography;

const MESES_DE_BUSCA = 12;

const janelaDeBusca = () =>
  periodoSemiaberto([
    dayjs().subtract(MESES_DE_BUSCA, "month").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD"),
  ]);

const ESTILO_DO_CARD = { width: "85%" };
const ESTILOS_DO_CARD = {
  header: { backgroundColor: "var(--ouran-card-header-bg)" },
};

export default function PromocoesPorSku() {
  const { user } = useAuth();
  const [parametros, setParametros] = useSearchParams();
  const eventoDaUrl = Number(parametros.get("evento")) || null;
  const [selectedAnalysisDays, setSelectedAnalysisDays] =
    useState(JANELA_VIGENCIA);
  const [selectedBaseline, setSelectedBaseline] = useState(null);
  const [eventId, setEventId] = useState(eventoDaUrl);

  useEffect(() => {
    setSelectedAnalysisDays(JANELA_VIGENCIA);
    setSelectedBaseline(null);
    setEventId(null);
  }, [user?.active_branch_id]);

  const escolherEvento = (valor) => {
    setEventId(valor);
    if (parametros.has("evento")) {
      parametros.delete("evento");
      setParametros(parametros, { replace: true });
    }
  };

  const periodo = useMemo(janelaDeBusca, []);

  const { data, isFetching, error } = useEventsV2({
    start: periodo?.start,
    end: periodo?.end,
  });

  const eventosPorProduto = useMemo(
    () => (data?.events || []).filter(analisePorProduto),
    [data]
  );

  const opcoes = useMemo(
    () => opcoesDeEventos(eventosPorProduto),
    [eventosPorProduto]
  );

  useEffect(() => {
    if (opcoes.length === 0) {
      setEventId(null);
      return;
    }
    if (!opcoes.some((opcao) => opcao.value === eventId)) {
      setEventId(
        opcoes.some((opcao) => opcao.value === eventoDaUrl)
          ? eventoDaUrl
          : opcoes[0].value
      );
    }
  }, [opcoes, eventId, eventoDaUrl]);

  const [evento, setEvento] = useState(null);

  useEffect(() => {
    setEvento(
      eventosPorProduto.find((item) => item.event_id === eventId) || null
    );
  }, [eventosPorProduto, eventId]);

  const semPromocoes = !isFetching && opcoes.length === 0;
  const eventoComVigencia = evento ? !!evento.end_datetime : null;
  const { period, janela } = parametrosDaJanela(
    selectedAnalysisDays,
    eventoComVigencia
  );

  const seletorDePromocao = (
    <>
      <Text strong style={{ marginBottom: 6, lineHeight: 1.2 }}>
        Promoção:
      </Text>
      <Select
        size="small"
        style={{ width: "320px" }}
        value={eventId}
        onChange={escolherEvento}
        options={opcoes}
        showSearch
        optionFilterProp="label"
        loading={isFetching}
        disabled={semPromocoes}
        placeholder={
          semPromocoes
            ? "Nenhuma promoção encontrada"
            : "Busque pelo nome ou pela vigência"
        }
        notFoundContent="Nenhuma promoção com esse termo"
      />
      <Text type="secondary" style={{ marginTop: 6, fontSize: 12 }}>
        {isFetching
          ? "Carregando promoções…"
          : `${opcoes.length} ${
              opcoes.length === 1 ? "promoção" : "promoções"
            } nos últimos ${MESES_DE_BUSCA} meses`}
      </Text>
    </>
  );

  return (
    <Flex
      vertical
      gap={50}
      justify="center"
      align="center"
      style={{ width: "100%", margin: "0 auto" }}>
      <Filters
        page="promocoes_por_sku"
        selectedAnalysisDays={selectedAnalysisDays}
        setSelectedAnalysisDays={setSelectedAnalysisDays}
        selectedBaseline={selectedBaseline}
        setSelectedBaseline={setSelectedBaseline}
        filters={["dias_analise"]}
        extraControls={seletorDePromocao}
        eventoComVigencia={eventoComVigencia}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          style={ESTILO_DO_CARD}
          message={mensagemDeErro(error)}
        />
      )}

      {evento ? (
        <>
          <CabecalhoDoEvento
            evento={evento}
            onTituloAlterado={(titulo) =>
              setEvento((atual) => (atual ? { ...atual, titulo } : atual))
            }
          />
          <EventoPorProdutoDetalhe
            evento={evento}
            period={period}
            janela={janela}
            baseline={selectedBaseline}
          />
        </>
      ) : (
        !error && (
          <Card style={ESTILO_DO_CARD} styles={ESTILOS_DO_CARD}>
            {isFetching ? (
              <Flex justify="center" style={{ padding: 32 }}>
                <Spin />
              </Flex>
            ) : (
              <Empty
                description={`Esta loja não teve promoção por SKU nos últimos ${MESES_DE_BUSCA} meses.`}
              />
            )}
          </Card>
        )
      )}
    </Flex>
  );
}
